import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '../api';

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const [status, setStatus]  = useState('CHECKING');
  const [txn,    setTxn]     = useState(null);
  const navigate = useNavigate();

  const txnId   = searchParams.get('txn');
  const gateway = searchParams.get('gateway') || 'phonepe'; // razorpay payment links pass ?gateway=razorpay

  useEffect(() => {
    if (!txnId) { setStatus('ERROR'); return; }

    const endpoint = gateway === 'razorpay'
      ? `/razorpay/status/${txnId}`
      : `/payment/status/${txnId}`;

    const check = async () => {
      try {
        const { data } = await API.get(endpoint);
        setStatus(data.status || 'PENDING');
        setTxn(data.transaction);
      } catch {
        setStatus('ERROR');
      }
    };

    check();

    // Poll every 3 s while PENDING (max 30 s)
    const interval = setInterval(async () => {
      try {
        const { data } = await API.get(endpoint);
        if (data.status !== 'PENDING') {
          setStatus(data.status);
          setTxn(data.transaction);
          clearInterval(interval);
        }
      } catch { clearInterval(interval); }
    }, 3000);

    const timeout = setTimeout(() => clearInterval(interval), 30000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [txnId, gateway]);

  const configs = {
    CHECKING: { icon: '⏳', color: 'var(--pending)',  title: 'Checking Payment...',   sub: 'Please wait while we verify your payment.' },
    SUCCESS:  { icon: '✅', color: 'var(--success)',  title: 'Payment Successful!',    sub: 'Your payment has been received and confirmed.' },
    PENDING:  { icon: '🕐', color: 'var(--pending)',  title: 'Payment Pending',        sub: 'Your payment is being processed. Please wait.' },
    FAILED:   { icon: '❌', color: 'var(--error)',    title: 'Payment Failed',          sub: 'Your payment could not be processed. Please try again.' },
    ERROR:    { icon: '⚠️', color: 'var(--warning)', title: 'Something Went Wrong',    sub: 'We could not verify your payment status.' },
  };

  const cfg = configs[status] || configs.PENDING;

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)',
      backgroundImage: 'radial-gradient(ellipse at 50% 30%, rgba(124,92,252,0.08) 0%, transparent 60%)'
    }}>
      <div style={{
        textAlign: 'center', padding: 40,
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 20, width: '100%', maxWidth: 420,
        boxShadow: 'var(--shadow-lg)'
      }} className="fade-in">

        <div style={{ fontSize: 64, marginBottom: 16 }}>{cfg.icon}</div>

        <h2 style={{ fontFamily: 'Syne,sans-serif', fontSize: 24, fontWeight: 800, color: cfg.color, marginBottom: 8 }}>
          {cfg.title}
        </h2>
        <p style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 24 }}>{cfg.sub}</p>

        {/* Gateway badge */}
        <div style={{ marginBottom: 16 }}>
          <span style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
            background: gateway === 'razorpay' ? '#eff5ff' : '#f3eeff',
            color: gateway === 'razorpay' ? '#2c74f6' : '#6739b7',
            border: `1px solid ${gateway === 'razorpay' ? '#c2d9ff' : '#ddd4ff'}`,
          }}>
            {gateway === 'razorpay' ? '💳 Razorpay' : '📱 PhonePe'}
          </span>
        </div>

        {status === 'CHECKING' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <span className="spinner" style={{ width: 32, height: 32, borderWidth: 3, borderTopColor: 'var(--primary)' }} />
          </div>
        )}

        {txn && (
          <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 16, marginBottom: 20, textAlign: 'left' }}>
            {[
              ['Amount',   `₹${parseFloat(txn.amount).toLocaleString('en-IN')}`],
              ['Customer', txn.customer_name],
              ['Phone',    txn.customer_phone],
              ['Txn ID',   txn.merchant_transaction_id],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)' }}>{k}</span>
                <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: k === 'Amount' ? 15 : 13 }}>{v}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          {status === 'FAILED' && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/create-payment')}>
              Try Again
            </button>
          )}
          {status === 'SUCCESS' && (
            <button className="btn btn-gold btn-sm" onClick={() => navigate('/transactions')}>
              View Transactions
            </button>
          )}
        </div>
      </div>
    </div>
  );
}